import type { ChatLocalDevelopmentOptions } from '@plivo/chat-client'
import { REGIONS } from 'v2/configs/constants'
import { resolveBaseUrl } from 'v2/shared/services/create-service'
import { getLocalAuth } from 'v2/utils'

export interface BuddyFlowApp {
  appId: string
  appKey: string
}

export interface BuddyFlowConfig extends BuddyFlowApp {
  region: string
  apiOrigin?: string
  localDevelopment?: ChatLocalDevelopmentOptions
}

export interface BuddyFlowEnv {
  region?: string
  // The console's regional gateway, with any {{region}} placeholder already resolved.
  gatewayUrl?: string
  apps: Record<string, BuddyFlowApp>
  localAttachmentOrigin?: string
  localAttachmentWireOrigin?: string
}

// Read per call so tests can set them. Each read stays a literal `process.env.REACT_APP_*`
// reference, because vite-plugin-environment only replaces those.
export const readBuddyFlowApps = (): Record<string, BuddyFlowApp> => ({
  [REGIONS.US]: {
    appId: process.env.REACT_APP_BUDDY_FLOW_APP_ID_US_EAST_1 || '',
    appKey: process.env.REACT_APP_BUDDY_FLOW_APP_KEY_US_EAST_1 || '',
  },
  [REGIONS.IN]: {
    appId: process.env.REACT_APP_BUDDY_FLOW_APP_ID_AP_SOUTH_1 || '',
    appKey: process.env.REACT_APP_BUDDY_FLOW_APP_KEY_AP_SOUTH_1 || '',
  },
})

const isLoopbackOrigin = (origin: string): boolean => {
  try {
    const { hostname } = new URL(origin)
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
  } catch {
    return false
  }
}

const originOf = (url: string): string | undefined => {
  try {
    return new URL(url).origin
  } catch {
    return undefined
  }
}

// Null when this region has no Buddy chat app, so the caller keeps the aiassist panel.
export const resolveBuddyFlowConfig = (env: BuddyFlowEnv): BuddyFlowConfig | null => {
  const app = env.region ? env.apps[env.region] : undefined
  if (!env.region || !app?.appId || !app.appKey) return null
  const gateway = env.gatewayUrl ? originOf(env.gatewayUrl) : undefined
  const config: BuddyFlowConfig = { ...app, region: env.region }
  if (!gateway) return config
  if (!isLoopbackOrigin(gateway)) return { ...config, apiOrigin: gateway }
  // The SDK only accepts a loopback API origin through its local-development option,
  // which also needs the browser-reachable origin of the local attachment store.
  const attachmentOrigin = env.localAttachmentOrigin || gateway
  return {
    ...config,
    localDevelopment: {
      apiOrigin: gateway,
      attachmentOrigin,
      ...(env.localAttachmentWireOrigin
        ? { attachmentWireOrigin: env.localAttachmentWireOrigin }
        : {}),
    },
  }
}

const resolveGatewayUrl = (): string | undefined => {
  try {
    return resolveBaseUrl()
  } catch {
    return undefined
  }
}

export const getBuddyFlowConfig = (): BuddyFlowConfig | null =>
  resolveBuddyFlowConfig({
    region: getLocalAuth()?.region,
    gatewayUrl: resolveGatewayUrl(),
    apps: readBuddyFlowApps(),
    localAttachmentOrigin: process.env.REACT_APP_BUDDY_FLOW_LOCAL_ATTACHMENT_ORIGIN || '',
    localAttachmentWireOrigin: process.env.REACT_APP_BUDDY_FLOW_LOCAL_ATTACHMENT_WIRE_ORIGIN || '',
  })
